<template>
  <div class="results-panel">
    <div class="results-title">
      Resultados
      <span v-if="total !== undefined" class="results-count">
        ({{ total }})
      </span>
    </div>
    <div v-if="loading" class="loading-container">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
      <p>Cargando resultados...</p>
    </div>
    <div v-else-if="items && items.length > 0" class="results-grid">
      <div
        v-for="(item, index) in items"
        :key="item.id"
        class="result-item"
        :class="{ 'result-item--selected': selectedItemId !== null && item.id === selectedItemId }"
      >
        <div class="image-container" @click="$emit('image-click', item)">
          <img
            :src="getImageUrl(item.imageUrl)"
            :alt="item.kalan_pmiName"
            class="result-image"
            @load="onImageLoad($event, item)"
            :ref="`image-${item.id}`"
          />
          <div
            v-if="item.rectangle"
            class="rectangle-overlay"
            :style="getRectangleStyle(item.rectangle, item.id)"
          ></div>
          <span
            v-if="item.similarity != null"
            class="result-similarity"
            :class="similarityClass(item.similarity)"
          >
            {{ formatSimilarity(item.similarity) }}%
          </span>
          <div class="item-number-badge">#{{ index + 1 }}</div>
          <v-btn
            icon
            x-small
            dark
            class="fullscreen-btn"
            title="Ver imagen completa"
            @click.stop="$emit('image-fullscreen', item)"
          >
            <v-icon small>mdi-fullscreen</v-icon>
          </v-btn>
        </div>
        <div class="image-info">
          <div class="image-date">
            {{ formatDateTime(item.collectTime) }}
          </div>
          <div
            class="image-subtitle"
            :class="{ 'image-subtitle--clickable': cameraNameClickable }"
            :title="cameraNameClickable ? 'Centrar y abrir el PMI en el mapa' : null"
            @click="cameraNameClickable && $emit('camera-name-click', item)"
          >
            {{ item.kalan_pmiName }} - {{ item.kalan_cameraDev }}
          </div>
        </div>
      </div>
    </div>
    <div v-else class="search-results-placeholder">
      <p v-if="searchQuery">
        Resultados de búsqueda inteligente para: "{{ searchQuery }}"
      </p>
      <p v-else-if="placeholderText">
        {{ placeholderText }}
      </p>
    </div>
  </div>
</template>

<script>
import moment from '@/util/dayjs-config';

export default {
  name: 'AISearchResultsGrid',
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    total: {
      type: Number,
      default: undefined,
    },
    searchQuery: {
      type: String,
      default: '',
    },
    placeholderText: {
      type: String,
      // Default = mensaje legacy para no romper consumidores que no pasan
      // la prop (Map.vue legacy). Pasar '' explícito = no mostrar nada.
      default: 'Ingresa un texto de consulta y haz clic en "Actualizar búsqueda" para ver resultados',
    },
    getImageUrl: {
      type: Function,
      default(url) {
        if (!url) return '';
        const u = url.replace('/images/', '/api/intelli/images/');
        if (u.startsWith('http://') || u.startsWith('https://')) return u;
        return u;
      },
    },
    selectedItemId: {
      type: [String, Number],
      default: null,
    },
    // Cuando true, el nombre de la cámara (subtitle) es clickeable y emite
    // 'camera-name-click' con el item — el consumidor (SmartSearchPanel/map-v2)
    // lo usa para centrar el mapa y abrir el PMI. Off por defecto para no
    // afectar a los consumidores legacy (AISearchModal, AISearch.vue): ahí el
    // nombre no se vuelve clickeable.
    cameraNameClickable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      imageDimensions: {},
    };
  },
  methods: {
    formatSimilarity(s) {
      const v = Number(s || 0);
      return Math.round(v > 1 ? v : v * 100);
    },
    similarityClass(s) {
      const p = this.formatSimilarity(s);
      if (p >= 90) return 'result-similarity--high';
      if (p >= 80) return 'result-similarity--mid';
      return 'result-similarity--low';
    },
    formatDateTime(dateTimeString) {
      if (!dateTimeString) return '';
      return moment(dateTimeString).format('DD/MM/YYYY HH:mm:ss');
    },
    getRectangleStyle(rectangleString, itemId) {
      try {
        const rect = typeof rectangleString === 'string' ? JSON.parse(rectangleString) : rectangleString;
        if (!rect || typeof rect !== 'object') {
          return { display: 'none' };
        }

        const imgDims = this.imageDimensions[itemId];

        if (
          imgDims
          && imgDims.naturalWidth
          && imgDims.naturalHeight
          && imgDims.displayWidth
          && imgDims.displayHeight
        ) {
          const containerAspect = imgDims.displayWidth / imgDims.displayHeight;
          const imageAspect = imgDims.naturalWidth / imgDims.naturalHeight;

          let scale = 1;
          let offsetX = 0;
          let offsetY = 0;

          if (imageAspect > containerAspect) {
            scale = imgDims.displayWidth / imgDims.naturalWidth;
            offsetY = (imgDims.displayHeight - imgDims.naturalHeight * scale) / 2;
          } else {
            scale = imgDims.displayHeight / imgDims.naturalHeight;
            offsetX = (imgDims.displayWidth - imgDims.naturalWidth * scale) / 2;
          }

          const rectLeft = (rect.x || 0) * imgDims.naturalWidth * scale + offsetX;
          const rectTop = (rect.y || 0) * imgDims.naturalHeight * scale + offsetY;
          const rectWidth = (rect.width || 0) * imgDims.naturalWidth * scale;
          const rectHeight = (rect.height || 0) * imgDims.naturalHeight * scale;

          const leftPercent = (rectLeft / imgDims.displayWidth) * 100;
          const topPercent = (rectTop / imgDims.displayHeight) * 100;
          const widthPercent = (rectWidth / imgDims.displayWidth) * 100;
          const heightPercent = (rectHeight / imgDims.displayHeight) * 100;

          return {
            position: 'absolute',
            left: `${Math.max(0, leftPercent)}%`,
            top: `${Math.max(0, topPercent)}%`,
            width: `${Math.min(100, widthPercent)}%`,
            height: `${Math.min(100, heightPercent)}%`,
            border: '2px solid #ff0000',
            boxSizing: 'border-box',
            pointerEvents: 'none',
            zIndex: 10,
          };
        }

        const leftPercent = (rect.x || 0) * 100;
        const topPercent = (rect.y || 0) * 100;
        const widthPercent = (rect.width || 0) * 100;
        const heightPercent = (rect.height || 0) * 100;

        return {
          position: 'absolute',
          left: `${leftPercent}%`,
          top: `${topPercent}%`,
          width: `${widthPercent}%`,
          height: `${heightPercent}%`,
          border: '2px solid #ff0000',
          boxSizing: 'border-box',
          pointerEvents: 'none',
          zIndex: 10,
        };
      } catch (error) {
        console.error('Error parsing rectangle:', error);
        return { display: 'none' };
      }
    },
    onImageLoad(event, item) {
      if (event && event.target && item) {
        this.$nextTick(() => {
          this.$set(this.imageDimensions, item.id, {
            naturalWidth: event.target.naturalWidth,
            naturalHeight: event.target.naturalHeight,
            displayWidth: event.target.offsetWidth || event.target.clientWidth,
            displayHeight: event.target.offsetHeight || event.target.clientHeight,
          });
          this.$forceUpdate();
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.results-panel {
  flex: 1;
  min-width: 0;
  padding: 14px;
  overflow-y: auto;
}

.results-title {
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 20px;
  color: #ffffff !important;
}

.results-count {
  font-size: 14px;
  font-weight: normal;
  color: rgba(255, 255, 255, 0.7);
  margin-left: 8px;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: rgba(255, 255, 255, 0.7);

  p {
    margin-top: 16px;
    color: rgba(255, 255, 255, 0.7);
  }
}

.search-results-placeholder {
  padding: 20px;
  text-align: center;
  color: rgba(255, 255, 255, 0.7) !important;

  p {
    color: rgba(255, 255, 255, 0.7) !important;
  }
}

.results-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  padding: 10px 0;
}

.result-item {
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid var(--map-card-border);
  border-radius: 8px;
  overflow: hidden;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }

  &.result-item--selected {
    outline: 2px solid var(--v-primary-base, #1f5dc0);
    outline-offset: -2px;
  }
}

.image-container {
  position: relative;
  width: 100%;
  padding-top: 75%;
  background-color: rgba(0, 0, 0, 0.3);
  overflow: hidden;
  cursor: pointer;

  .result-image {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .rectangle-overlay {
    position: absolute;
    pointer-events: none;
    z-index: 10;
  }

  .result-similarity {
    position: absolute;
    top: 6px;
    right: 6px;
    padding: 2px 6px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 600;
    background: rgba(0, 0, 0, 0.65);
    color: #fff;
    z-index: 11;

    &--high { background: var(--kln-success, #1e9553); }
    &--mid  { background: var(--kln-warning, #c98a1c); }
  }

  .item-number-badge {
    position: absolute;
    top: 6px;
    left: 6px;
    background: rgba(0, 0, 0, 0.7);
    color: #ffffff;
    font-family: "SF Mono", "JetBrains Mono", Menlo, monospace;
    font-size: 11px;
    font-weight: 700;
    padding: 2px 6px;
    border-radius: 2px;
    pointer-events: none;
    z-index: 11;
  }

  /* fullscreen-btn: bottom-right para no chocar con result-similarity arriba */
  .fullscreen-btn {
    position: absolute !important;
    bottom: 4px;
    right: 4px;
    background: rgba(0, 0, 0, 0.5) !important;
    opacity: 0;
    transition: opacity 0.15s;
    z-index: 11;
  }
}

.image-container:hover .fullscreen-btn,
.result-item--selected .image-container .fullscreen-btn {
  opacity: 1;
}

.image-info {
  padding: 12px;
  background-color: rgba(0, 0, 0, 0.2);

  .image-date {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.9);
    margin-bottom: 4px;
    font-weight: 500;
  }

  .image-subtitle {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.4;
  }

  .image-subtitle--clickable {
    cursor: pointer;
    text-decoration: underline;
    text-decoration-color: rgba(255, 255, 255, 0.25);
    text-underline-offset: 2px;
    transition: color 0.12s ease;
  }
  .image-subtitle--clickable:hover {
    color: var(--kln-accent, #16C4C7);
    text-decoration-color: var(--kln-accent, #16C4C7);
  }
}
</style>
